import ReactDatePicker from 'react-datepicker'
import './MonthPicker.scss'
import { forwardRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Icon } from '@/ui'
import { sortBookkeepingReportsByYear } from '@/selectors/reportSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DateTime } from 'luxon'
import { faCalendar } from '@fortawesome/pro-solid-svg-icons'
interface DateType {
  value?: string
  onClick?: () => void
}

const CustomInput = forwardRef(({ value, onClick }: DateType, _: unknown) => (
  <Button variant="secondary" onClick={onClick}>
    <Icon icon={faCalendar} />
    &nbsp;
    {value}
  </Button>
))

export const MonthPicker = ({ activeMonth }: { activeMonth: DateTime }) => {
  const sortedBookkeepingReports = useReselector(sortBookkeepingReportsByYear)
  const navigate = useNavigate()

  const startDate = useMemo(
    () => DateTime.local(activeMonth.year, activeMonth.month).toJSDate(),
    [activeMonth]
  )

  const reportIdMap = useMemo(() => {
    if (!sortedBookkeepingReports) return null
    const reportMap: { [date: string]: string | number } = {}
    Object.keys(sortedBookkeepingReports).forEach((key) => {
      const reportsForYear = sortedBookkeepingReports[key]
      reportsForYear.forEach((report) => {
        reportMap[
          DateTime.fromISO(report.startDate, { zone: 'utc' }).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR
          )
        ] = report.id
      })
    })
    return reportMap
  }, [sortedBookkeepingReports])

  const reportMonths = useMemo(() => {
    if (!reportIdMap) return [] as Date[]
    return Object.keys(reportIdMap).map((dateStr) =>
      DateTime.fromFormat(dateStr, DATE_FORMATS_LUXON.MONTH_YEAR).toJSDate()
    )
  }, [reportIdMap])

  const onChange = (date: Date | null) => {
    if (!date) {
      return
    }
    const month = DateTime.fromJSDate(date).toFormat(
      DATE_FORMATS_LUXON.MONTH_ZERO_PADDED
    )
    const year = DateTime.fromJSDate(date).toFormat(
      DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT
    )
    navigate(`/reports/bookkeeping/${month}/${year}/overview`)
  }

  return (
    <div className="monthPicker">
      <ReactDatePicker
        selected={startDate}
        onChange={onChange}
        dateFormat={DATE_FORMATS_LUXON.MONTH_YEAR_SHORT}
        showMonthYearPicker
        // Without this, the calendar renders off screen
        withPortal
        includeDates={reportMonths}
        customInput={<CustomInput />}
      />
    </div>
  )
}
